import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, Tag } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export const TipologiasFacturas = () => {
  const { API, token } = useContext(AuthContext);
  const [tipologiasGastos, setTipologiasGastos] = useState([]);
  const [tipologiasProductos, setTipologiasProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [tipologiaEditando, setTipologiaEditando] = useState(null);
  const [tipoTipologiaModal, setTipoTipologiaModal] = useState('gastos');
  const [nombreTipologia, setNombreTipologia] = useState('');
  const [descripcionTipologia, setDescripcionTipologia] = useState('');

  useEffect(() => {
    cargarTipologiasGastos();
    cargarTipologiasProductos();
  }, []);

  const cargarTipologiasGastos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tipologias/gastos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTipologiasGastos(response.data.filter(t => t.activa));
    } catch (error) {
      toast.error('Error al cargar tipologías de gastos');
    } finally {
      setLoading(false);
    }
  };

  const cargarTipologiasProductos = async () => {
    try {
      const response = await axios.get(`${API}/tipologias/productos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTipologiasProductos(response.data.filter(t => t.activa));
    } catch (error) {
      toast.error('Error al cargar tipologías de productos');
    }
  };

  const abrirModalTipologia = (tipo, tipologia = null) => {
    setTipoTipologiaModal(tipo);
    if (tipologia) {
      setTipologiaEditando(tipologia);
      setNombreTipologia(tipologia.nombre);
      setDescripcionTipologia(tipologia.descripcion || '');
    } else {
      setTipologiaEditando(null);
      setNombreTipologia('');
      setDescripcionTipologia('');
    }
    setModalAbierto(true);
  };

  const cerrarModalTipologia = () => {
    setModalAbierto(false);
    setTipologiaEditando(null);
    setNombreTipologia('');
    setDescripcionTipologia('');
  };

  const guardarTipologia = async () => {
    if (!nombreTipologia.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      const data = {
        nombre: nombreTipologia.trim(),
        descripcion: descripcionTipologia.trim() || null
      };

      if (tipologiaEditando) {
        await axios.put(
          `${API}/tipologias/${tipoTipologiaModal}/${tipologiaEditando.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Tipología actualizada exitosamente');
      } else {
        await axios.post(
          `${API}/tipologias/${tipoTipologiaModal}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Tipología creada exitosamente');
      }

      cerrarModalTipologia();
      if (tipoTipologiaModal === 'gastos') {
        await cargarTipologiasGastos();
      } else {
        await cargarTipologiasProductos();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar tipología');
    }
  };

  const eliminarTipologia = async (tipo, id) => {
    if (!confirm('¿Estás seguro de que deseas eliminar esta tipología?')) {
      return;
    }

    try {
      await axios.delete(`${API}/tipologias/${tipo}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tipología eliminada exitosamente');
      if (tipo === 'gastos') {
        await cargarTipologiasGastos();
      } else {
        await cargarTipologiasProductos();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar tipología');
    }
  };

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Tag size={28} />
            Tipologías de Facturas
          </h1>
          <p className="text-gray-600 mt-2">
            Gestiona las categorías para clasificar tus facturas recibidas y emitidas
          </p>
        </div>

        <Card className="p-6">
          <Tabs defaultValue="gastos" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="gastos">Tipologías de Gastos</TabsTrigger>
              <TabsTrigger value="productos">Tipologías de Productos</TabsTrigger>
            </TabsList>
            
            <TabsContent value="gastos" className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">
                  Categorías para facturas recibidas (gastos)
                </p>
                <Button onClick={() => abrirModalTipologia('gastos')}>
                  <Plus size={16} className="mr-2" />
                  Nueva Tipología
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-8">Cargando...</div>
              ) : tipologiasGastos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No hay tipologías de gastos. Crea una para empezar.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tipologiasGastos.map((tipologia) => (
                    <Card key={tipologia.id} className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{tipologia.nombre}</h3>
                          {tipologia.descripcion && (
                            <p className="text-sm text-gray-600 mt-1">{tipologia.descripcion}</p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => abrirModalTipologia('gastos', tipologia)}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => eliminarTipologia('gastos', tipologia.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="productos" className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">
                  Categorías para facturas emitidas (productos/servicios)
                </p>
                <Button onClick={() => abrirModalTipologia('productos')}>
                  <Plus size={16} className="mr-2" />
                  Nueva Tipología
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-8">Cargando...</div>
              ) : tipologiasProductos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No hay tipologías de productos. Crea una para empezar.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tipologiasProductos.map((tipologia) => (
                    <Card key={tipologia.id} className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{tipologia.nombre}</h3>
                          {tipologia.descripcion && (
                            <p className="text-sm text-gray-600 mt-1">{tipologia.descripcion}</p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => abrirModalTipologia('productos', tipologia)}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => eliminarTipologia('productos', tipologia.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>

        {/* Modal de Tipología */}
        <Dialog open={modalAbierto} onOpenChange={setModalAbierto}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {tipologiaEditando ? 'Editar' : 'Crear'} Tipología de {tipoTipologiaModal === 'gastos' ? 'Gastos' : 'Productos'}
              </DialogTitle>
              <DialogDescription>
                {tipologiaEditando ? 'Modifica los datos de la tipología' : 'Crea una nueva tipología para categorizar tus facturas'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  value={nombreTipologia}
                  onChange={(e) => setNombreTipologia(e.target.value)}
                  placeholder="Ej: Material de oficina, Consultoría..."
                />
              </div>
              <div>
                <Label htmlFor="descripcion">Descripción</Label>
                <Input
                  id="descripcion"
                  value={descripcionTipologia}
                  onChange={(e) => setDescripcionTipologia(e.target.value)}
                  placeholder="Descripción opcional..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={cerrarModalTipologia}>
                  Cancelar
                </Button>
                <Button onClick={guardarTipologia}>
                  {tipologiaEditando ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TipologiasFacturas;

